// Client-side persistence for the manual paper trader: the watchlist, the
// last-open account, and the legacy (pre-backend) engine state for migration.

const STATE_KEY = "aeon.paper.state.v1";
const WATCH_KEY = "aeon.paper.watchlists.v1";

const canStore = () => {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
};

export function loadState() {
  if (!canStore()) return null;
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveState(state) {
  if (!canStore()) return;
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — non-fatal, engine keeps working in memory */
  }
}

// Accounts now live on the backend (see manualApi.js). The old local state is
// only read once, to migrate browser-only accounts, then cleared.
export function clearState() {
  if (!canStore()) return;
  try { window.localStorage.removeItem(STATE_KEY); } catch {}
}

// Which account (manual id or engine account id) this device last had open.
const ACTIVE_KEY = "aeon.paper.active.v1";
export function loadActiveId() {
  if (!canStore()) return null;
  try { return window.localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
export function saveActiveId(id) {
  if (!canStore()) return;
  try { window.localStorage.setItem(ACTIVE_KEY, id); } catch {}
}

// ── Watchlists ──────────────────────────────────────────────────────────────
// A watchlist is { id, name, sections: [{ id, name, collapsed, symbols:[base] }] }

export const DEFAULT_WATCHLISTS = [
  {
    id: "default",
    name: "Watchlist",
    sections: [
      { id: "crypto", name: "CRYPTO", collapsed: false, symbols: ["BTC", "ETH", "SOL"] },
      { id: "alts", name: "ALTS", collapsed: false, symbols: ["BNB", "XRP", "DOGE"] },
    ],
  },
];

export function loadWatchlists() {
  if (!canStore()) return { lists: DEFAULT_WATCHLISTS, activeId: "default" };
  try {
    const raw = window.localStorage.getItem(WATCH_KEY);
    if (!raw) return { lists: DEFAULT_WATCHLISTS, activeId: "default" };
    const parsed = JSON.parse(raw);
    if (!parsed?.lists?.length) return { lists: DEFAULT_WATCHLISTS, activeId: "default" };
    return parsed;
  } catch {
    return { lists: DEFAULT_WATCHLISTS, activeId: "default" };
  }
}

export function saveWatchlists(data) {
  if (!canStore()) return;
  try {
    window.localStorage.setItem(WATCH_KEY, JSON.stringify(data));
  } catch {}
}

// Flatten all symbols across the active list's sections (for feed subscription).
export function symbolsOf(list) {
  if (!list) return [];
  const out = [];
  for (const sec of list.sections || []) for (const s of sec.symbols || []) if (!out.includes(s)) out.push(s);
  return out;
}
